import { services } from '@qompium/pages-helper-tools';
import { Currency, GroupTypesB2C } from '../helpers/product';
import * as apiClient from './apiClient';

export enum OneOffPaymentTag {
  B2C_ESSENTIAL_ONE_OFF_MONTH = 'b2c_essential_month',
  B2C_ESSENTIAL_ONE_OFF_YEAR = 'b2c_essential_year',
  B2C_PREMIUM_ONE_OFF_MONTH = 'b2c_premium_month',
  B2C_PREMIUM_ONE_OFF_YEAR = 'b2c_premium_year',
}

export enum RecurringPaymentTag {
  B2C_ESSENTIAL_MONTH = 'b2c_essential_month_recurring',
  B2C_ESSENTIAL_YEAR = 'b2c_essential_year_recurring',
  B2C_PREMIUM_MONTH = 'b2c_premium_month_recurring',
  B2C_PREMIUM_YEAR = 'b2c_premium_year_recurring',
}

export enum UpgradeTag {
  B2C_PREMIUM_UPGRADE_MONTH = 'b2c_premium_month_upgrade',
  B2C_PREMIUM_UPGRADE_YEAR_MONTH_1 = 'b2c_premium_year_upgrade_1',
  B2C_PREMIUM_UPGRADE_YEAR_MONTH_2 = 'b2c_premium_year_upgrade_2',
  B2C_PREMIUM_UPGRADE_YEAR_MONTH_3 = 'b2c_premium_year_upgrade_3',
  B2C_PREMIUM_UPGRADE_YEAR_MONTH_4 = 'b2c_premium_year_upgrade_4',
  B2C_PREMIUM_UPGRADE_YEAR_MONTH_5 = 'b2c_premium_year_upgrade_5',
  B2C_PREMIUM_UPGRADE_YEAR_MONTH_6 = 'b2c_premium_year_upgrade_6',
  B2C_PREMIUM_UPGRADE_YEAR_MONTH_7 = 'b2c_premium_year_upgrade_7',
  B2C_PREMIUM_UPGRADE_YEAR_MONTH_8 = 'b2c_premium_year_upgrade_8',
  B2C_PREMIUM_UPGRADE_YEAR_MONTH_9 = 'b2c_premium_year_upgrade_9',
  B2C_PREMIUM_UPGRADE_YEAR_MONTH_10 = 'b2c_premium_year_upgrade_10',
  B2C_PREMIUM_UPGRADE_YEAR_MONTH_11 = 'b2c_premium_year_upgrade_11',
  B2C_PREMIUM_UPGRADE_YEAR_MONTH_12 = 'b2c_premium_year_upgrade_12',
}

const paymentsServiceUrl = services.payments;

export async function getProducts() {
  const endpoint = 'products';
  const tags = Object.values({ ...OneOffPaymentTag, ...RecurringPaymentTag, ...UpgradeTag });
  const filter = `in(tags,${tags.join()})`;
  const select = 'select(id,price,tags,currency)';

  return await apiClient.getAll<Product>(paymentsServiceUrl, `${endpoint}?${filter}&${select}`);
}

export async function getPaymentMethod(userId: string) {
  const endpoint = `stripe/users/${userId}`;

  const response = await apiClient.get<PaymentMethodResponse>(paymentsServiceUrl, endpoint);

  return filterPreferredPaymentMethod(response?.paymentMethods);
}

function filterPreferredPaymentMethod(paymentMethods: PaymentMethod[] | undefined) {
  if (!paymentMethods) {
    return undefined;
  }
  const mostRecentPaymentMethod = paymentMethods.sort((a, b) => new Date(b.updateTimestamp).getTime() - new Date(a.updateTimestamp).getTime())[0];

  return mostRecentPaymentMethod;
}

export async function postPaymentIntent(userId: string, productId: string, data: any, paymentMethod: PaymentMethod | undefined, currency: Currency) {
  const endpoint = 'stripe/paymentIntents';

  const paymentIntentData: any = {
    productId,
    targetUserId: userId,
    data,
    tags: ['recurring_payment'],
    currency,
  };

  if (paymentMethod) {
    paymentIntentData.paymentMethodId = paymentMethod.id;
  } else {
    paymentIntentData.setupPaymentMethodReuse = 'offSession' as const;
  }
  const order = await apiClient.post<PaymentIntentResponse>(paymentsServiceUrl, endpoint, paymentIntentData);

  return order?.stripeClientSecret || '';
}

export async function postPaymentMethod(userId: string, data: PaymentMethodRequestData) {
  const endpoint = `stripe/users/${userId}/paymentMethods`;

  return await apiClient.post<PaymentMethod>(paymentsServiceUrl, endpoint, data);
}

export async function createSetupIntent() {
  const endpoint = 'stripe/setupIntents';

  const setupIntentData = {
    setupPaymentMethodReuse: 'offSession' as const,
  };

  const order = await apiClient.post<any>(paymentsServiceUrl, endpoint, setupIntentData);
  return order?.stripeClientSecret || '';
}

export async function checkIfIap(userId: string, userConfiguration: any, activeGroupIds: string[]) {
  if (!userConfiguration.patientConfigurations ||
    userConfiguration.patientConfigurations.some(
      (config: any) => activeGroupIds.includes(config.groupId) && Object.values(GroupTypesB2C).includes(config.data.groupType)
    )
  ) {
    const entitlement = await apiClient.getFirst<Entitlement>(paymentsServiceUrl, 'subscriptions/entitlements' +
    `?userId=${userId}` +
    '&subscriptionGroup=fibricheck' +
    '&in(subscriptionTier,essential,premium)' +
    '&in(statusCategory,acquiring,engaged,active_but_losing)' +
    '&sort(-expireTimestamp)' +
    '&select(source)');

    if (entitlement && entitlement.source) {
      switch (entitlement.source.toLowerCase()) {
        case 'appstore': return true;
        case 'playstore': return true;
        default: return false;
      }
    }
  }
  return false;
}

export interface Product {
  prices: any;
  id: string;
  tags: string[];
}

interface PaymentMethodRequestData {
  stripeId: string;
}

export interface PaymentMethod {
  id: string;
  stripeId: string;
  brand: string;
  expirationYear: number;
  expirationMonth: number;
  last4Digits: string;
  updateTimestamp: string;
}

export interface PaymentMethodResponse {
  paymentMethods: PaymentMethod[];
}

export interface PaymentIntentResponse {
  id: string;
  stripeClientSecret: string;
}

interface Entitlement {
  id: string;
  userId: string;
  source: 'appstore' | 'playstore';
  status: string;
  subscriptionGroup: string;
  subscriptionTier: string;
  statusCategory: string;
  expireTimestamp: string;
}
